import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { Dispatch, useEffect, useRef } from "react";

import IconChevronLeft from "@/icons/icon-chevron-left";
import { useBreakpoint } from "~/contexts/breakpoint";
import CrossCountry from "~/types/cross-country";

import Dialog from "../common/dialog";
import Icon from "../common/icon";
import NotifyMe from "../notify-me/notify-me";
import ProductColorsDialog from "./product-colors-dialog";
import styles from "./product-tile.module.scss";
import productTileDialogStyles from "./product-tile-dialog.module.scss";

type Props = {
  source: CrossCountry.ChildrenProduct | CrossCountry.Product;
  product: CrossCountry.Product;
  setSource: Dispatch<CrossCountry.ChildrenProduct | CrossCountry.Product>;
  currentDialog: "colors" | "notifyMe" | null;
  setCurrentDialog: Dispatch<"colors" | "notifyMe" | null>;
  setFocusOnSelectorBtn?: Dispatch<React.SetStateAction<boolean>>;
  hasChildren?: boolean;
  _trackItem?: any;
  idxInList?: number;
  listScope?: string;
  isAlgolia?: boolean;
};
export default function ProductTileDialog(props: Props) {
  const t = useTranslations();
  const breakpoint = useBreakpoint();
  const refNotifyMe = useRef<HTMLDivElement>(null);
  const backButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // a11y - focus on back arrow on panel opening
    if (!!props.currentDialog && props.hasChildren) {
      backButton.current?.focus();
    }
  }, [props.currentDialog, props.hasChildren]);

  const colorsDialogContent = (
    <>
      {!!props.currentDialog && props.hasChildren && (
        <ProductColorsDialog
          products={props.product.children!}
          current={props.source}
          onChange={() => props.setCurrentDialog("colors")}
          onSelect={(newProduct) => {
            props.setSource(newProduct);
            if (newProduct.has_stock) {
              props?._trackItem &&
                props?._trackItem("view_item_list", props, newProduct, {
                  user_action: "impression_change_color",
                });
              props.setCurrentDialog(null);
            } else {
              props.setCurrentDialog("notifyMe");
            }
          }}
          onClose={() => props.setCurrentDialog(null)}
          showColorList={props.hasChildren && props.currentDialog === "colors"}
          isAlgolia={!!props.isAlgolia}
          backendId={props.product.backend_id}
        />
      )}
      {!props.source.has_stock && props.currentDialog === "notifyMe" && (
        <NotifyMe
          ref={refNotifyMe}
          productId={props.source?.product_id}
          showClose={false}
          className={productTileDialogStyles.notifyMeContainer}
        />
      )}
    </>
  );
  return (
    <>
      {breakpoint === "mobile" && (
        <Dialog
          isOpen={!!props.currentDialog}
          title={
            <>
              <p className={styles.actionsPaneSubTitle}>{"color" in props.source ? props.source.color : ""}</p>
              {props.hasChildren && <p className={styles.actionsPaneTitle}>{t("generic.product_tiles.all_shades")}</p>}
            </>
          }
          customStyles={productTileDialogStyles}
          onClose={() => props.setCurrentDialog(null)}
          shouldFocusCloseBtn
        >
          {colorsDialogContent}
        </Dialog>
      )}
      {breakpoint === "desktop" && (
        <AnimatePresence>
          {!!props.currentDialog && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className={styles.actionsPaneContainer}
              role="dialog"
              aria-modal="true"
              aria-labelledby="color-selector-dialog"
              id="listbox-panel"
              tabIndex={-1}
              onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.key === "Escape") {
                  props.setCurrentDialog(null);
                  props.setFocusOnSelectorBtn?.(true);
                }
              }}
            >
              <div className={styles.actionsPaneButton}>
                <button
                  ref={backButton}
                  aria-label={t("generic.back")}
                  onClick={() => {
                    props.setCurrentDialog(null);
                    props.setFocusOnSelectorBtn?.(true);
                  }}
                >
                  <Icon icon={IconChevronLeft} width="16" height="16" aria-hidden />
                </button>
                <p className={styles.actionsPaneTitle} id="color-selector-dialog">
                  {props.hasChildren && props.currentDialog !== "notifyMe"
                    ? t("generic.product_tiles.all_shades")
                    : t("generic.product_tiles.back")}
                </p>
              </div>
              <div className={styles.actionsPanelContent}>{colorsDialogContent}</div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </>
  );
}
